import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import {
  useBriefing,
  useLeadScores,
  useCallOptimizer,
  useNicheIntelligence,
  useAnomalies,
  useScrapeAdvisor,
  useDealScores,
} from '../../hooks/useAIAnalytics';
import { useAICapability } from '../../hooks/useAICapability';
import type { AICapabilityState } from '../../types';

interface CapRow {
  capability: string;
  endpoint: string;
  layer: 'Analytics' | 'Generative';
  state: AICapabilityState;
}

const STATE_TONE: Record<AICapabilityState, BadgeTone> = {
  available: 'success',
  unavailable: 'warning',
  loading: 'neutral',
  error: 'danger',
};

const STATE_LABEL: Record<AICapabilityState, string> = {
  available: 'Available',
  unavailable: 'Unavailable',
  loading: 'Checking…',
  error: 'Error',
};

function toState(q: { isLoading: boolean; isError: boolean; data: unknown }): AICapabilityState {
  if (q.isLoading) return 'loading';
  if (q.isError) return 'error';
  return 'available';
}

export function CapabilitiesSection() {
  const briefing = useBriefing();
  const leadScores = useLeadScores();
  const dealScores = useDealScores();
  const callOpt = useCallOptimizer();
  const niche = useNicheIntelligence();
  const anomalies = useAnomalies();
  const scrape = useScrapeAdvisor();
  const gen = useAICapability();

  const rows: CapRow[] = [
    { capability: 'Daily Briefing', endpoint: 'GET /api/ai/briefing', layer: 'Analytics', state: toState(briefing) },
    { capability: 'Lead Scores', endpoint: 'GET /api/ai/lead-scores', layer: 'Analytics', state: toState(leadScores) },
    { capability: 'Deal Scores', endpoint: 'GET /api/ai/deal-scores', layer: 'Analytics', state: toState(dealScores) },
    { capability: 'Call Optimizer', endpoint: 'GET /api/ai/call-optimizer', layer: 'Analytics', state: toState(callOpt) },
    { capability: 'Niche Intelligence', endpoint: 'GET /api/ai/niche-intelligence', layer: 'Analytics', state: toState(niche) },
    { capability: 'Anomalies', endpoint: 'GET /api/ai/anomalies', layer: 'Analytics', state: toState(anomalies) },
    { capability: 'Scrape Advisor', endpoint: 'GET /api/ai/scrape-advisor', layer: 'Analytics', state: toState(scrape) },
    { capability: 'AI Chat', endpoint: 'POST /api/ai/chat', layer: 'Generative', state: gen.state },
    { capability: 'Follow-up Draft', endpoint: 'POST /api/ai/follow-up-draft', layer: 'Generative', state: gen.state },
    { capability: 'Meeting Agenda', endpoint: 'POST /api/ai/meeting-agenda', layer: 'Generative', state: gen.state },
    { capability: 'Lead Insights', endpoint: 'POST /api/ai/lead-insights', layer: 'Generative', state: gen.state },
    { capability: 'Call Coach', endpoint: 'POST /api/ai/call-coach', layer: 'Generative', state: gen.state },
    { capability: 'Intent Parser', endpoint: 'POST /api/ai/parse-intent', layer: 'Generative', state: gen.state },
  ];

  const columns: Column<CapRow>[] = [
    { key: 'capability', header: 'Capability', render: (r) => <span className="font-medium text-on-surface">{r.capability}</span> },
    { key: 'layer', header: 'Layer', render: (r) => <Badge tone={r.layer === 'Analytics' ? 'info' : 'primary'}>{r.layer}</Badge> },
    { key: 'endpoint', header: 'Endpoint', render: (r) => <span className="text-body-sm text-on-surface-variant">{r.endpoint}</span> },
    { key: 'state', header: 'Status', align: 'right', render: (r) => <Badge tone={STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</Badge> },
  ];

  return (
    <Card>
      <CardHeader title="AI Capabilities" />
      <CardBody className="space-y-3">
        <p className="text-body-sm text-on-surface-variant">
          Live status of each AI capability. Analytics run on real CRM data. Generative capabilities activate
          automatically once a backend AI provider is connected.
        </p>
        <Table columns={columns} data={rows} rowKey={(r) => r.capability} />
      </CardBody>
    </Card>
  );
}
